import React, { useState, useEffect, useMemo } from "react";
import styles from "./UserManagement.module.scss";
import {
    Button,
    Card,
    Col,
    Form,
    Modal,
    Row,
    Container,
    Table,
    InputGroup,
    Spinner,
} from "react-bootstrap";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import DataManager from "../../../General/DataManager";
import UserClass from "../../../General/LocalStorage/UserClass";
import Functions from "../../../General/Functions";
import TablePagination from "../../../Layouts/TablePagination/TablePagination";
import DualScrollTable from "../../../Layouts/DualScrollTable/DualScrollTable";
import { exportTicketsCsv, exportTicketsPdf } from "../BoardingTickets/ExportService";

// Per-column filters (filter row below the table header)
const EMPTY_COLUMN_FILTERS = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    role: "",
    report: "",
    status: "",
    createdAt: "",
    updatedAt: "",
};

// Case and accent insensitive comparison
const normalizeText = (value) =>
    String(value ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();

const formatDate = (value) => (value ? new Date(value).toLocaleString("it-IT") : "");

const UserManagement = () => {

    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState("");
    const [getListError, setGetListError] = useState(null);
    const [totalResults, setTotalResults] = useState(0);

    const roleLabels = {
        OPERATOR: t("role_operator"),
        DEVICE_MANAGER: t("role_device_manager"),
        OPERATOR_CHECKIN: t("role_operator_checkin"),
        OPERATOR_SUPERVISOR: t("role_operator_supervisor"),
        SYSADMIN: t("role_sysadmin"),
        BOARDING_OPERATOR: t("role_boarding_operator"),

    };


    const [columnFilters, setColumnFilters] = useState(EMPTY_COLUMN_FILTERS);
    const [allUsers, setAllUsers] = useState(null);
    const [loadingAllUsers, setLoadingAllUsers] = useState(false);

    const hasColumnFilters = Object.values(columnFilters).some(value => value.trim() !== "");

    const updateColumnFilter = (key, value) => {
        setColumnFilters(prev => ({ ...prev, [key]: value }));
        setPage(1);
    };

    const clearColumnFilters = () => {
        setColumnFilters(EMPTY_COLUMN_FILTERS);
        setPage(1);
    };

    const navigate = useNavigate();
    useEffect(() => {
        // While column filters are active the list is filtered/paginated client-side
        if (hasColumnFilters) return;
        getUsers(page, search);
    }, [page, search, limit, hasColumnFilters]);

    // Column filters need the full list (API only supports page/limit/search)
    useEffect(() => {
        if (!hasColumnFilters) {
            setAllUsers(null);
            return;
        }

        let cancelled = false;
        setLoadingAllUsers(true);

        fetchAllUsersForExport()
            .then(list => {
                if (!cancelled) setAllUsers(list);
            })
            .catch(error => {
                if (!cancelled) {
                    setAllUsers([]);
                    Functions.alertMsg(error?.message || t("msgErrorGeneral"));
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingAllUsers(false);
            });

        return () => {
            cancelled = true;
        };
    }, [hasColumnFilters, search]);


    const getUsers = async (pageNumber = 1, searchValue = search) => {

        let instance = UserClass.getInstance();
        let token = instance.getTokenUser();
        const { data, msgError } = await DataManager.getUsers(token, {
            page: pageNumber,
            limit: limit,
            search: searchValue
        });

        if (msgError) {
            setGetListError(msgError);
            return;
        }
        console.log("results: ", data["results"])
        setUsers(data["results"])
        setTotalPages(data["pagination"]["totalPages"]);
        setPage(data["pagination"]["page"]);
        setTotalResults(data.pagination.totalResult);

    }

    const [exporting, setExporting] = useState(null);

    // Table is paginated server-side, so fetch every page matching the current search for export
    const fetchAllUsersForExport = async () => {
        const token = UserClass.getInstance().getTokenUser();
        const exportLimit = 100;
        let allUsers = [];
        let currentPage = 1;
        let lastPage = 1;

        do {
            const { data, msgError } = await DataManager.getUsers(token, {
                page: currentPage,
                limit: exportLimit,
                search: search
            });

            if (msgError) throw new Error(msgError);

            allUsers = allUsers.concat(data?.results || []);
            lastPage = data?.pagination?.totalPages || 1;
            currentPage++;
        } while (currentPage <= lastPage);

        return allUsers;
    };

    const buildExportRows = (list) => list.map(user => ({
        [t("firstName")]: user?.firstName ?? '-',
        [t("lastName")]: user?.lastName ?? '-',
        [t("email")]: user?.email ?? '-',
        [t("phone")]: user?.phoneNumber ?? '-',
        [t("role")]: roleLabels[user?.role] ?? user?.role ?? '-',
        [t("report")]: user?.reports?.length
            ? user.reports.map(report => {
                const flags = [
                    report.defaultSending ? t("default") : null,
                    report.appearInSendingList ? t("sending_list") : null,
                ].filter(Boolean);
                return flags.length ? `${report.name} (${flags.join(", ")})` : report.name;
            }).join(", ")
            : '-',
        [t("status")]: user?.status ?? '-',
        [t("created_at")]: user?.createdAt ? new Date(user.createdAt).toLocaleString("it-IT") : '-',
        [t("updated_at")]: user?.updatedAt ? new Date(user.updatedAt).toLocaleString("it-IT") : '-',
    }));

    const matchesColumnFilters = (user) => {
        const f = columnFilters;
        const textMatch = (value, filter) =>
            !filter.trim() || normalizeText(value).includes(normalizeText(filter));

        return (
            textMatch(user?.firstName, f.firstName) &&
            textMatch(user?.lastName, f.lastName) &&
            textMatch(user?.email, f.email) &&
            textMatch(user?.phoneNumber, f.phoneNumber) &&
            (!f.role || user?.role === f.role) &&
            textMatch((user?.reports || []).map(report => report.name).join(" "), f.report) &&
            (!f.status || user?.status === f.status) &&
            textMatch(formatDate(user?.createdAt), f.createdAt) &&
            textMatch(formatDate(user?.updatedAt), f.updatedAt)
        );
    };

    const filteredAllUsers = useMemo(
        () => (allUsers || []).filter(matchesColumnFilters),
        [allUsers, columnFilters]
    );

    // What the table and pagination show: server page, or client-filtered page
    const displayUsers = hasColumnFilters
        ? filteredAllUsers.slice((page - 1) * limit, page * limit)
        : users;
    const displayTotalResults = hasColumnFilters ? filteredAllUsers.length : totalResults;
    const displayTotalPages = hasColumnFilters
        ? Math.max(1, Math.ceil(filteredAllUsers.length / limit))
        : totalPages;

    const statusFilterOptions = useMemo(
        () => [...new Set(["ACTIVE", "INACTIVE", ...[...(users || []), ...(allUsers || [])]
            .map(user => user?.status)
            .filter(Boolean)])],
        [users, allUsers]
    );

    const handleExport = async (format) => {
        setExporting(format);
        try {
            const list = await fetchAllUsersForExport();
            const rows = buildExportRows(hasColumnFilters ? list.filter(matchesColumnFilters) : list);
            if (!rows.length) return;

            const fileName = `utenti_${new Date().toISOString().slice(0, 10)}`;

            if (format === "csv") {
                exportTicketsCsv(rows, fileName);
            } else {
                exportTicketsPdf({
                    rows,
                    fileName,
                    title: t("user_management"),
                });
            }
        } catch (error) {
            Functions.alertMsg(error?.message || t("msgErrorGeneral"));
        } finally {
            setExporting(null);
        }
    };


    return (
        <div className={styles.UserManagement}>
            <Container fluid="md">
                {/* Header */}
                <Row className="mb-4 align-items-center">
                    <Col>
                        <h2 className={styles.title}>
                            <i className="fa fa-users me-2"></i>
                            {t("user_management")}
                        </h2>
                        <p className={styles.subtitle}>{t("manage_users")}</p>
                    </Col>
                    <Col xs="auto" className="d-flex flex-wrap align-items-center gap-2">
                        <div className={styles.exportGroup} role="group" aria-label={t("export")}>
                            <button
                                type="button"
                                className={`${styles.exportButton} ${styles.exportCsv}`}
                                onClick={() => handleExport("csv")}
                                disabled={exporting !== null || displayTotalResults === 0}
                                title={t("export_csv")}
                            >
                                {exporting === "csv"
                                    ? <Spinner animation="border" size="sm" />
                                    : <i className="fa-solid fa-file-csv" aria-hidden="true"></i>}
                                <span>CSV</span>
                            </button>
                            <button
                                type="button"
                                className={`${styles.exportButton} ${styles.exportPdf}`}
                                onClick={() => handleExport("pdf")}
                                disabled={exporting !== null || displayTotalResults === 0}
                                title={t("export_pdf")}
                            >
                                {exporting === "pdf"
                                    ? <Spinner animation="border" size="sm" />
                                    : <i className="fa-solid fa-file-pdf" aria-hidden="true"></i>}
                                <span>PDF</span>
                            </button>
                        </div>

                        <Button className="btn-primary" onClick={() => navigate(`/pages/UserManagement/newUser`)
                        }>
                            <i className="fa fa-plus me-1"></i>
                            {t("add_user")}
                        </Button>
                    </Col>
                </Row>

                {/* Search */}
                <Card className="mb-4">
                    <Card.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Label className="fw-semibold">{t("search")}</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <i className="fa fa-search"></i>
                                    </InputGroup.Text>
                                    <Form.Control placeholder={t("search_by_name_email")} onChange={(e) => {
                                        setSearch(e.target.value.length >= 3 ? e.target.value : "");
                                        setPage(1)
                                    }} />
                                </InputGroup>
                            </Col>
                            {hasColumnFilters && (
                                <Col md={6} className="d-flex align-items-end justify-content-md-end mt-3 mt-md-0">
                                    <button
                                        type="button"
                                        className={styles.clearFiltersButton}
                                        onClick={clearColumnFilters}
                                    >
                                        <i className="fa-solid fa-filter-circle-xmark" aria-hidden="true"></i>
                                        <span>{t("clear_filters")}</span>
                                    </button>
                                </Col>
                            )}
                        </Row>
                    </Card.Body>
                </Card>

                {/* Users Table */}
                <Card>
                    <Card.Body className="p-0">
                        <DualScrollTable>
                        <Table hover className="align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>{t("firstName")}</th>
                                    <th>{t("lastName")}</th>
                                    <th>{t("email")}</th>
                                    <th>{t("phone")}</th>
                                    <th>{t("role")}</th>
                                    <th>{t("report")}</th>
                                    <th>{t("status")}</th>
                                    <th>{t("created_at")}</th>
                                    <th>{t("updated_at")}</th>
                                    {/*<th className="text-end">{t("actions")}</th>*/}
                                </tr>

                                {/* Column filters */}
                                <tr className={styles.filterRow}>
                                    {[
                                        { key: "firstName", label: t("firstName") },
                                        { key: "lastName", label: t("lastName") },
                                        { key: "email", label: t("email") },
                                        { key: "phoneNumber", label: t("phone") },
                                    ].map(({ key, label }) => (
                                        <th key={key}>
                                            <Form.Control
                                                type="search"
                                                size="sm"
                                                value={columnFilters[key]}
                                                onChange={(e) => updateColumnFilter(key, e.target.value)}
                                                placeholder={t("filter_placeholder")}
                                                aria-label={`${t("filter_placeholder")} ${label}`}
                                                className={`${styles.columnFilter} ${columnFilters[key] ? styles.columnFilterActive : ""}`}
                                            />
                                        </th>
                                    ))}
                                    <th>
                                        <Form.Select
                                            size="sm"
                                            value={columnFilters.role}
                                            onChange={(e) => updateColumnFilter("role", e.target.value)}
                                            aria-label={`${t("filter_placeholder")} ${t("role")}`}
                                            className={`${styles.columnFilter} ${columnFilters.role ? styles.columnFilterActive : ""}`}
                                        >
                                            <option value="">{t("all")}</option>
                                            {Object.entries(roleLabels).map(([value, label]) => (
                                                <option key={value} value={value}>{label}</option>
                                            ))}
                                        </Form.Select>
                                    </th>
                                    <th>
                                        <Form.Control
                                            type="search"
                                            size="sm"
                                            value={columnFilters.report}
                                            onChange={(e) => updateColumnFilter("report", e.target.value)}
                                            placeholder={t("filter_placeholder")}
                                            aria-label={`${t("filter_placeholder")} ${t("report")}`}
                                            className={`${styles.columnFilter} ${columnFilters.report ? styles.columnFilterActive : ""}`}
                                        />
                                    </th>
                                    <th>
                                        <Form.Select
                                            size="sm"
                                            value={columnFilters.status}
                                            onChange={(e) => updateColumnFilter("status", e.target.value)}
                                            aria-label={`${t("filter_placeholder")} ${t("status")}`}
                                            className={`${styles.columnFilter} ${columnFilters.status ? styles.columnFilterActive : ""}`}
                                        >
                                            <option value="">{t("all")}</option>
                                            {statusFilterOptions.map(status => (
                                                <option key={status} value={status}>{status}</option>
                                            ))}
                                        </Form.Select>
                                    </th>
                                    {[
                                        { key: "createdAt", label: t("created_at") },
                                        { key: "updatedAt", label: t("updated_at") },
                                    ].map(({ key, label }) => (
                                        <th key={key}>
                                            <Form.Control
                                                type="search"
                                                size="sm"
                                                value={columnFilters[key]}
                                                onChange={(e) => updateColumnFilter(key, e.target.value)}
                                                placeholder="gg/mm/aaaa"
                                                aria-label={`${t("filter_placeholder")} ${label}`}
                                                className={`${styles.columnFilter} ${columnFilters[key] ? styles.columnFilterActive : ""}`}
                                            />
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {hasColumnFilters && loadingAllUsers && (
                                    <tr>
                                        <td colSpan={9} className="text-center text-muted py-4">
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            {t("loading")}
                                        </td>
                                    </tr>
                                )}
                                {hasColumnFilters && !loadingAllUsers && displayUsers.length === 0 && (
                                    <tr>
                                        <td colSpan={9} className="text-center text-muted py-4">
                                            <i className="fa-solid fa-filter me-2" aria-hidden="true"></i>
                                            {t("no_matching_results")}
                                        </td>
                                    </tr>
                                )}
                                {!(hasColumnFilters && loadingAllUsers) && displayUsers?.map((user) => (
                                    <tr key={user.id} className={styles.clickableRow}
                                        onClick={() => navigate(`/pages/UserManagement/Details/${user.id}`)
                                        }
                                    >
                                        <td>{user.firstName}</td>
                                        <td>{user.lastName}</td>
                                        <td>{user.email}</td>
                                        <td>{user.phoneNumber}</td>
                                        <td>{roleLabels[user?.role]}</td>
                                        <td>
                                            {user.reports?.length ? (
                                                user.reports.map(report => (
                                                    <div key={report.id} className="mb-1">
                                                        <strong>{report.name}</strong>

                                                        {report.defaultSending && (
                                                            <span className="badge bg-success ms-2">
                                                                {t("default")}
                                                            </span>
                                                        )}

                                                        {report.appearInSendingList && (
                                                            <span className="badge bg-info ms-1">
                                                                {t("sending_list")}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))
                                            ) : (
                                                "-"
                                            )}
                                        </td>
                                        <td>
                                            <span
                                                className={
                                                    user.status === "ACTIVE"
                                                        ? styles.active
                                                        : styles.inactive
                                                }
                                            >
                                                {user.status}
                                            </span>
                                        </td>
                                        <td>
                                            {user?.createdAt ? new Date(user?.createdAt).toLocaleString("it-IT") : ''}
                                        </td>
                                        <td>
                                            {user?.updatedAt ? new Date(user?.updatedAt).toLocaleString("it-IT") : ''}
                                        </td>


                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        </DualScrollTable>

                    </Card.Body>
                </Card>

                {/* Pagination */}
                <TablePagination
                    page={page}
                    totalPages={displayTotalPages}
                    totalResults={displayTotalResults}
                    limit={limit}
                    onPageChange={setPage}
                    onLimitChange={(size) => {
                        setLimit(size);
                        setPage(1);
                    }}
                />

            </Container>
        </div>
    );
};

export default UserManagement;